import { TEXT_COLORS, theme } from "@monthly/theme";
import HighchartsReact from "highcharts-react-official";
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

import { addWatermarkToHighchartsExport } from "../chartWatermark";
import Highcharts from "../HighChartsConfig";
import {
  formatNumberWithSeparators,
  truncateNumber,
} from "../utils/formatting";

interface WaterfallDataPoint {
  name: string;
  y?: number;
  isSum?: boolean;
  isIntermediateSum?: boolean;
}

interface WaterfallChartProps {
  data: WaterfallDataPoint[];
  height?: number;
  showLegend?: boolean;
  /**
   * Color for positive values
   * @default theme.palette.greenc.main
   */
  positiveColor?: string;
  /**
   * Color for negative values
   * @default theme.palette.redc.main
   */
  negativeColor?: string;
  /**
   * Color for sum values
   * @default theme.palette.s800.main
   */
  sumColor?: string;
  /**
   * Number of decimal places to display
   * @default 2
   */
  decimalPlaces?: number;
  isPercentage?: boolean;
  units?: string;
}

export interface WaterfallChartRef {
  downloadImage: (filename?: string) => void;
}

export const WaterfallChart = forwardRef<
  WaterfallChartRef,
  WaterfallChartProps
>(
  (
    {
      data,
      height = 280,
      showLegend = false,
      positiveColor = theme.palette.greenc.main,
      negativeColor = theme.palette.redc.main,
      sumColor = theme.palette.secondary[800],
      decimalPlaces = 1,
      isPercentage = true,
      units,
    },
    ref,
  ) => {
    const chartRef = useRef<HighchartsReact.RefObject>(null);

    useImperativeHandle(ref, () => ({
      downloadImage: (filename = "waterfall-chart") => {
        if (chartRef.current?.chart) {
          addWatermarkToHighchartsExport(chartRef.current.chart, filename);
        }
      },
    }));

    // Process data to assign colors dynamically and truncate values
    const processedData = data.map((point) => {
      const truncatedY =
        point.y !== undefined
          ? truncateNumber(point.y, decimalPlaces)
          : undefined;

      if (point.isSum) {
        return {
          name: point.name,
          y: truncatedY,
          isSum: true,
          color: sumColor,
        };
      }
      if (point.isIntermediateSum) {
        if (truncatedY !== undefined) {
          // y explícito: mostrar como barra de referencia (Highcharts ignora y para isIntermediateSum)
          return {
            name: point.name,
            y: truncatedY,
            color: sumColor,
          };
        }
        // Sin y: Highcharts calcula la suma acumulada en este punto
        return {
          name: point.name,
          isIntermediateSum: true,
          color: sumColor,
        };
      }
      // For intermediate values, use positive color for gains, negative for losses
      return {
        name: point.name,
        y: truncatedY,
        color: (truncatedY ?? 0) >= 0 ? positiveColor : negativeColor,
      };
    });
    const options: Highcharts.Options = {
      chart: {
        type: "waterfall",
        height,
        backgroundColor: "transparent",
      },
      title: {
        text: undefined,
      },
      xAxis: {
        type: "category",
        labels: {
          style: {
            ...theme.typography.caption,
            color: theme.palette.text.primary,
          },
        },
        lineColor: "transparent",
        tickWidth: 0,
      },
      yAxis: {
        visible: false,
        title: {
          text: undefined,
        },
        labels: {
          enabled: false,
        },
        gridLineWidth: 0,
      },
      legend: {
        enabled: showLegend,
      },
      tooltip: {
        backgroundColor: "#FFFFFF",
        borderColor: "#E0E2E7",
        borderRadius: 8,
        shadow: {
          color: "rgba(155, 160, 185, 0.2)",
          offsetX: 0,
          offsetY: 2,
          width: 8,
        },
        style: {
          fontFamily: `${theme.typography.caption.fontFamily}`,
          fontSize: `${theme.typography.caption.fontSize}`,
          fontWeight: `${theme.typography.caption.fontWeight}`,
          color: theme.palette.text.primary,
        },
        formatter: function () {
          const rawValue = (this as any).y ?? 0;
          const formatted = formatNumberWithSeparators(rawValue, decimalPlaces);
          const categoryName = (this as any).key || "";
          const pointColor = (this as any).color || sumColor;
          const suffix = isPercentage ? "%" : units;
          return `<div style="padding: 4px 0;">
          <span style="color: ${TEXT_COLORS.secondary}; font-size: 11px;">${categoryName}</span><br/>
          <span style="color: ${pointColor}; font-weight: 600; font-size: 14px;">${formatted}${suffix}</span>
        </div>`;
        },
        useHTML: true,
      },
      plotOptions: {
        waterfall: {
          borderWidth: 0,
          borderRadius: 4,
          pointPadding: 0.1,
          groupPadding: 0.15,
          dataLabels: {
            enabled: true,
            formatter: function () {
              const rawValue = (this as any).y ?? 0;
              const formatted = formatNumberWithSeparators(
                rawValue,
                decimalPlaces,
              );
              return `${formatted}${isPercentage ? "%" : ""}`;
            },
            inside: false,
            verticalAlign: "top",
            y: -20,
            overflow: "allow" as const,
            crop: false,
            style: {
              fontFamily: `${theme.typography.caption.fontFamily}`,
              fontSize: `${theme.typography.caption.fontSize}`,
              fontWeight: `${theme.typography.caption.fontWeight}`,
              color: theme.palette.text.primary,
              textOutline: "none",
            },
          },
          lineColor: positiveColor,
          lineWidth: 2,
        },
      },
      series: [
        {
          type: "waterfall",
          name: "Margen Bruto",
          data: processedData,
          upColor: positiveColor,
          color: negativeColor,
        },
      ],
    };

    useEffect(() => {
      // Reflow chart after initial render and after dialog animation completes
      const timer1 = setTimeout(() => {
        const chart = chartRef.current?.chart;
        if (chart) {
          chart.reflow();
        }
      }, 50);

      // Second reflow after fonts are fully loaded and dialog animation completes
      const timer2 = setTimeout(() => {
        const chart = chartRef.current?.chart;
        if (chart) {
          chart.reflow();
          // Force redraw to apply correct fonts
          chart.redraw();
        }
      }, 350);

      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
      };
    }, [data, height]);

    return (
      <HighchartsReact
        highcharts={Highcharts}
        options={options}
        ref={chartRef}
      />
    );
  },
);

WaterfallChart.displayName = "WaterfallChart";
